/**
 * DegreeAutocomplete — search degrees SCOPED to a chosen university.
 * Uses GET /api/degrees?university_id=<id>&title=<q>. Disabled until
 * a university is selected so we never spam the global index.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";


export default function DegreeAutocomplete({
  universityId,
  value, // { id, title } | null
  onSelect,
  placeholder = "Start typing your course…",
  testIdPrefix = "deg-ac",
}) {
  const [q, setQ] = useState(value?.title || "");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setQ(value?.title || "");
  }, [value?.id]); // eslint-disable-line

  useEffect(() => {
    // Reset when the parent uni changes.
    setQ(value?.title || "");
    setRows([]);
    setOpen(false);
  }, [universityId]); // eslint-disable-line

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const search = (text) => {
    clearTimeout(debounceRef.current);
    if (!universityId || !text || text.trim().length < 2) {
      setRows([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/degrees?university_id=${encodeURIComponent(universityId)}` +
          `&title=${encodeURIComponent(text.trim())}&limit=10`,
        );
        setRows(Array.isArray(data) ? data.slice(0, 10) : []);
        setOpen(true);
      } catch {
        setRows([]);
      }
    }, 220);
  };

  const clear = () => {
    setQ("");
    setRows([]);
    setOpen(false);
    onSelect?.(null);
  };

  const disabled = !universityId;

  return (
    <div className="relative" ref={containerRef}>
      <Input
        value={q}
        onChange={(e) => {
          const v = e.target.value;
          setQ(v);
          if (value?.id) onSelect?.(null);
          search(v);
        }}
        onFocus={() => q.length >= 2 && setOpen(true)}
        placeholder={disabled ? "Pick a university first" : placeholder}
        disabled={disabled}
        data-testid={`${testIdPrefix}-input`}
      />
      {value?.id && (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-stone-100"
          onClick={clear}
          data-testid={`${testIdPrefix}-clear`}
          aria-label="Clear degree"
        >
          <X className="h-3.5 w-3.5 text-stone-500" />
        </button>
      )}
      {open && rows.length > 0 && (
        <ul
          className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-stone-200 bg-white shadow-lg"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {rows.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => {
                  onSelect?.({ id: d.id, title: d.official_title || d.title || d.id });
                  setQ(d.official_title || d.title || d.id);
                  setOpen(false);
                }}
                data-testid={`${testIdPrefix}-option-${d.id}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50"
              >
                <div className="truncate">{d.official_title || d.title || d.id}</div>
                {d.subject_area && (
                  <div className="text-[10px] font-mono text-stone-400 uppercase truncate">
                    {d.subject_area}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
